import { DocumentAddIcon, ChatAlt2Icon, ChatAltIcon, PhoneIcon } from "@heroicons/react/solid";
import { hideTooltips } from "../settings/theme/ThemeTooltip";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { userProfile } from "../../features/user/selectors";
import SupportTicketForm from "./forms/SupportTicketForm";
import BaseModal from "./modal/BaseModal";
import { filterOn } from "../../util/ibuki";
import axios from "axios";

export const FeedbackTab = () => {
	const profile = useSelector(userProfile);
	const [showFeedback, setShowFeedback] = useState(false);

	useEffect(() => {
		if (profile && profile.originatingSystemID) {
			// Hide the feedback button for SWLAR users
			if (["SWLAR"].includes(profile.originatingSystemID)) {
				setShowFeedback(false);
			} else {
				setShowFeedback(true);
			}
		}
	}, [profile]);

	return showFeedback ? (
		<div className="fixed -right-8 top-1/2 bg-transparent transform rotate-90 z-20">
			<a href="https://go.crmls.org/crmls-user-feedback/" target="_blank" rel="noreferrer">
				<div className="bg-header text-white rounded-b-md cursor-pointer tracking-wide px-2 py-2 opacity-80 hover:opacity-100 flex justify-center items-center -mt-4">
					<ChatAltIcon className="h-6 w-6 mr-1" /> Feedback
				</div>
			</a>
		</div>
	) : (
		<></>
	);
};

export const SupportTabs = (props) => {
	const [openSupportTicket, setOpenSupportTicket] = useState(false);
	const [openPhoneModal, setOpenPhoneModal] = useState(false);
	const [authMessageCode, setAuthMessageCode] = useState();
	const [codeVerifier, setCodeVerifier] = useState();
	const [isAssociationSupport, setIsAssociationSupport] = useState(false);
	const authRedirectUri = `https://${process.env.REACT_APP_ROOT_URL}`;

	const generateRandomString = (length) => {
		var text = "";
		var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

		for (var i = 0; i < length; i++) {
			text += possible.charAt(Math.floor(Math.random() * possible.length));
		}

		return text;
	}

	async function generateCodeChallenge(codeVerifier) {
		var digest = await crypto.subtle.digest("SHA-256",
			new TextEncoder().encode(codeVerifier));

		return btoa(String.fromCharCode(...new Uint8Array(digest)))
			.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
	}

	// Listeners for support ticket form and chat
	useEffect(() => {
		let supportChatSub, supportTicketSub = undefined;

		// Listeners for support chat and ticket events
		supportChatSub = filterOn("openSupportChat").subscribe(() => handleChat());
		supportTicketSub = filterOn("openSupportTicket").subscribe((props) => handleSupportTicket(props.data.isAssociationSupport ?? false));

		// Retrieve the auth code for Genesys authenicated messaging
		const codeVerifier = generateRandomString(64);
		Promise.resolve()
			.then(() => {
				return generateCodeChallenge(codeVerifier)
			})
			.then(function (codeChallenge) {
				axios.post(`${process.env.REACT_APP_AUTH_URL}/api/authorize`, {
					client_id: "genesys.webmessaging.crmls",
					redirect_uri: "https://unused.callback.local/callback",
					scope: "ODataApi openid profile CrmlsProfile MemberPortalApi offline_access",
					response_type: "code",
					code_challenge: codeChallenge,
					code_challenge_method: "S256",
				},
					{
						withCredentials: true,
						headers: {
							'Access-Control-Allow-Origin': '*/*',
							'Content-Type': 'application/json'
						}
					}).then((response) => {
						if (response?.data?.code) {
							setAuthMessageCode(response.data.code)
							setCodeVerifier(codeVerifier)
						}
					});
			});

		return () => {
			supportChatSub && supportChatSub.unsubscribe();
			supportTicketSub && supportTicketSub.unsubscribe();
		};
	}, []);

	useEffect(() => {
		let surveyConfigScript;
		if (!window.Genesys) {
			(function (g, e, n, es, ys) {
				g["_genesysJs"] = e;
				g[e] =
					g[e] ||
					function () {
						(g[e].q = g[e].q || []).push(arguments);
					};
				g[e].t = 1 * new Date();
				g[e].c = es;
				ys = document.createElement("script");
				ys.async = 1;
				ys.src = n;
				ys.charset = "utf-8";
				document.head.appendChild(ys);
			})(window, "Genesys", process.env.REACT_APP_GENESYS_SCRIPT_URL, { environment: process.env.REACT_APP_GENESYS_ENVIRONMENT, deploymentId: process.env.REACT_APP_GENESYS_DEPLOYMENT_ID });
		}

		// Add genesys survey script to head
		const surveyScript = document.createElement("script");
		surveyScript.src = `${process.env.REACT_APP_GENESYS_SURVEY_SCRIPT_URL}`;
		surveyScript.addEventListener('load', function () {
			surveyConfigScript = document.createElement("script");
			const surveyConfigContent = document.createTextNode(
				`SDXWebMessengerSurvey.showSurvey({
					"survey_url": "${process.env.REACT_APP_GENESYS_SURVEY_URL}",
					"trigger_type": "${process.env.REACT_APP_GENESYS_SURVEY_TRIGGER}",
					"minimum_messages": ${process.env.REACT_APP_GENESYS_SURVEY_MESSAGE_COUNT}
				});`
			);
			surveyConfigScript.appendChild(surveyConfigContent);
			document.head.appendChild(surveyConfigScript);
		});
		document.head.appendChild(surveyScript);

		return () => {
			document.head.removeChild(surveyScript);
			if (surveyConfigScript) document.head.removeChild(surveyConfigScript);
		};
	}, []);

	useEffect(() => {
		let authMessageLogout;
		if (window.Genesys && authMessageCode && codeVerifier) {
			window.Genesys('registerPlugin', 'AuthProvider', (AuthProvider) => {
				AuthProvider.registerCommand('getAuthCode', (e) => {
					e.resolve({
						authCode: authMessageCode,
						redirectUri: "https://unused.callback.local/callback",
						codeVerifier: codeVerifier
					});
				});
				AuthProvider.registerCommand('reAuthenticate', (e) => {
					// Brand can handle this how ever they want.
					// This command will be called when current refreshToken and/or authCode are no more valid. User needs to re-login here to provide us new authCode and/or refreshToken.
					// ndAuthLoginBtn.click(); // simulate the login click.
					e.resolve();
				});

				AuthProvider.subscribe('Auth.ready', () => {
					console.log('Auth.ready')
				});

				AuthProvider.subscribe('Auth.authenticated', () => {
					console.log('Auth.authenticated')
				});

				AuthProvider.subscribe('Auth.error', (error) => {
					// 'message' here is provided by Genesys API and its always expected to be in string. Any security scan results are false postive in this case.
					const { message } = error.data || false;
					console.error("Auth.error", message ?? error);
				});

				AuthProvider.subscribe('Auth.authError', (error) => {
					// 'message' here is provided by Genesys API and its always expected to be in string. Any security scan results are false postive in this case.
					const { message } = error.data || false;
					console.error("Auth.authError", message ?? error);
				});

				// Listen for logOut and also log out of Genesys auth
				authMessageLogout = filterOn("logOut").subscribe(() => {
					AuthProvider.command('Auth.logout')
						.finally(() => {
							console.log("Auth.logout success")
						});
				});

				AuthProvider.ready();
			});
		}

		return () => {
			authMessageLogout && authMessageLogout.unsubscribe();
		};
	}, [authMessageCode, codeVerifier]);

	const handleChat = () => {
		if (window.Genesys) {
			window.Genesys(
				"command",
				"Messenger.open",
				{},
				function (o) { },
				function (o) {
					window.Genesys("command", "Messenger.close");
				}
			);
		}
	};

	const handlePhone = () => {
		// Open phone
		setOpenPhoneModal(true);
		hideTooltips();
	};

	const handleSupportTicket = (associationSupport) => {
		// Open support ticket form
		setIsAssociationSupport(associationSupport ?? false)
		setOpenSupportTicket(true);
		hideTooltips();
	};

	const handleSupportFormClose = () => {
		setIsAssociationSupport(false);
		setOpenSupportTicket(false);
	}

	return props.layout === "tabs" ? (
		<>
			<div className="absolute right-16 -top-10 bg-transparent transform">
				<div className="bg-primary text-header rounded-t-md flex justify-center items-center border border-divider border-b-0">
					<div className="p-2 cursor-pointer opacity-80 hover:opacity-100" data-tip="Call" onClick={handlePhone}>
						<PhoneIcon className="h-6 w-6" />
					</div>
					{authMessageCode && (
						<div className="p-2 cursor-pointer opacity-80 hover:opacity-100" data-tip="Live Chat" onClick={handleChat}>
							<ChatAlt2Icon className="h-6 w-6" />
						</div>
					)}
					<div className="p-2 cursor-pointer opacity-80 hover:opacity-100" data-tip="Create Ticket" onClick={() => handleSupportTicket(false)}>
						<DocumentAddIcon className="h-6 w-6" />
					</div>
				</div>
			</div>
			<SupportTicketForm title="Create Ticket" open={openSupportTicket} setOpen={handleSupportFormClose} isAssociationSupport={isAssociationSupport} />
			<BaseModal open={openPhoneModal} setOpen={setOpenPhoneModal}>
				<div className="p-4">
					<h3 className="text-lg font-semibold">Call Support</h3>
					<p className="text-sm">
						Please call the support team at <a href={`tel:${props.supportPhone.replace(/\D+/g, "")}`}>{props.supportPhone}</a>.
					</p>
				</div>
			</BaseModal>
		</>
	) : props.layout === "flyout" ? (
		<>
			<div className="fixed bottom-24 sm:bottom-4 right-4 invisible md:visible">
				<div className="relative rounded-lg border border-divider bg-primary px-2 py-1 shadow-sm flex items-center space-x-3 shadow-lg sm:px-4 sm:py-3 text-primary">
					<ul className="support-menu absolute w-full h-full">
						<li className="bg-header cursor-pointer" data-tip="Create Ticket" onClick={() => handleSupportTicket(false)}>
							<DocumentAddIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
						</li>
						{authMessageCode && (
							<li className="bg-header cursor-pointer" data-tip="Live Chat" onClick={handleChat}>
								<ChatAlt2Icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
							</li>
						)}
						<li className="bg-header cursor-pointer" data-tip="Call" onClick={handlePhone}>
							<PhoneIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
						</li>
					</ul>
					<div className="flex-shrink-0">
						<div className="flex justify-items-center">
							<ChatAlt2Icon className="h-6 w-6 sm:h-8 sm:w-8 text-header" />
						</div>
					</div>
					<div className="flex-1 min-w-0">
						<span className="absolute inset-0" aria-hidden="true" />
						<p className="text-sm font-medium">Support</p>
						<p className="hidden text-sm truncate xl:block">Questions? We're here to help.</p>
					</div>
				</div>
			</div>
			<div className="fixed bottom-16 right-4 visible md:invisible w-1/2">
				<div className="flex justify-between">
					<div className="p-2 bg-header rounded-full">
						<PhoneIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white bg-header rounded-full" onClick={handlePhone} />
					</div>
					{authMessageCode && (
						<div className="p-2 bg-header rounded-full">
							<ChatAlt2Icon className="h-6 w-6 sm:h-8 sm:w-8 text-white bg-header rounded-full" onClick={handleChat} />
						</div>
					)}
					<div className="p-2 bg-header rounded-full">
						<DocumentAddIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" onClick={() => handleSupportTicket(false)} />
					</div>
				</div>
			</div>
			<SupportTicketForm title="Create Ticket" open={openSupportTicket} setOpen={handleSupportFormClose} isAssociationSupport={isAssociationSupport} />
			<BaseModal open={openPhoneModal} setOpen={setOpenPhoneModal}>
				<div className="p-4">
					<h3 className="text-lg font-semibold">Call Support</h3>
					<p className="text-sm">
						Please call the support team at <a href={`tel:${props.supportPhone.replace(/\D+/g, "")}`}>{props.supportPhone}</a>.
					</p>
				</div>
			</BaseModal>
		</>
	) : (
		<>
			<div className="fixed bottom-24 sm:bottom-4 right-4">
				<div className="relative rounded-lg border border-divider bg-primary px-2 py-1 shadow-sm flex items-center space-x-3 shadow-lg sm:px-4 sm:py-3 text-header">
					<div className="rounded-full flex justify-items-center opacity-80 hover:opacity-100 cursor-pointer" data-tip="Call" onClick={handlePhone}>
						<PhoneIcon className="h-6 w-6" />
					</div>
					{authMessageCode && (
						<div className="rounded-full flex justify-items-center opacity-80 hover:opacity-100 cursor-pointer" data-tip="Live Chat" onClick={handleChat}>
							<ChatAlt2Icon className="h-6 w-6" />
						</div>
					)}
					<div className="rounded-full flex justify-items-center opacity-80 hover:opacity-100 cursor-pointer" data-tip="Create Ticket" onClick={() => handleSupportTicket(false)}>
						<DocumentAddIcon className="h-6 w-6" />
					</div>
				</div>
			</div>
			<SupportTicketForm title="Create Ticket" open={openSupportTicket} setOpen={handleSupportFormClose} isAssociationSupport={isAssociationSupport} />
			<BaseModal open={openPhoneModal} setOpen={setOpenPhoneModal}>
				<div className="p-4">
					<h3 className="text-lg font-semibold">Call Support</h3>
					<p className="text-sm">
						Please call the support team at <a href={`tel:${props.supportPhone.replace(/\D+/g, "")}`}>{props.supportPhone}</a>.
					</p>
				</div>
			</BaseModal>
		</>
	);
};
